import React from 'react';

export function StatusBadge({ value }: { value?: string | null }) {
  if (!value) return <span className="text-xs text-gray-400">—</span>;
  const upper = String(value).toUpperCase();
  const cls =
    upper === 'ACTIVE' || upper === 'SENT' || upper === 'DELIVERED' || upper === 'READ'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      : upper === 'PAUSED' || upper === 'SCHEDULED' || upper === 'QUEUED' || upper === 'PENDING'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
        : upper === 'FAILED'
          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
          : upper === 'CANCELLED' || upper === 'CANCELED' || upper === 'STOPPED'
            ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{value}</span>
  );
}

export function StatCard({
  label,
  value,
  hint,
  onViewList,
}: {
  label: string;
  value: string | number;
  hint?: string;
  onViewList?: () => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-900">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-gray-500">{hint}</p> : null}
      {onViewList ? (
        <button
          type="button"
          onClick={onViewList}
          className="mt-1.5 text-[11px] font-medium text-[#009de2] hover:underline"
        >
          View list
        </button>
      ) : null}
    </div>
  );
}

export function formatDateTime(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function toDatetimeLocalValue(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EmptyState({
  title,
  description,
  tone = 'neutral',
}: {
  title: string;
  description?: string;
  tone?: 'neutral' | 'amber' | 'violet';
}) {
  const toneCls =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-950/20'
      : tone === 'violet'
        ? 'border-violet-200 bg-violet-50/80 dark:border-violet-900/40 dark:bg-violet-950/20'
        : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40';
  return (
    <div className={`rounded-2xl border px-6 py-12 text-center ${toneCls}`}>
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
      {description ? (
        <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">{description}</p>
      ) : null}
    </div>
  );
}

